<template>
  <div class="author-pane" v-if="!isEmpty(authors)">
    <div class="author-image">
      <div class="author-mask" :class="[borderColorClass]">
        <div class="image-border-mask">
          <term-link :tid="author.tid" :title="author.title" :url="author.relative_url" :type="author.type" v-if="authorImage">
            <img class="grayscale" :class="{'multi-author': isMultiAuthor}" v-lazy="authorImage" />
          </term-link>
          <img v-else v-lazy="defaultImg" />
        </div>
      </div>
    </div>

    <div :class="['author-info', articleType]">
      <div v-if="author.title" class="author-list">
        <div class="authors" v-if="authors">
          <div v-for="(author, key, index) in authors" :key="index" class="author-name">
            <strong v-if="author.title === 'Abacus Digest Team'" class="author-info-name">
              {{author.title}}
            </strong>
            <term-link v-else :tid="author.tid" :title="author.title" :url="author.relative_url" :type="author.type" class="author-info-name">
              {{author.title}}
            </term-link>
            <onload-idle-queue class="author-info-box-wrapper" v-if="viewportIsDesktop && author.title != 'Abacus Digest Team'">
              <div :class="['author-info-box', articleType]">
                <div class="name">
                  <term-link :tid="author.tid" :title="author.title" :url="author.relative_url" :type="author.type">
                    {{author.title}}
                  </term-link>
                </div>
                <div class="role" v-if="get(author, 'role.text')">{{author.role.text}}</div>
                <div class="expertise" v-if="get(author, 'area_of_expertise')">
                  <svg-author-expert></svg-author-expert>
                  {{author.area_of_expertise.join(', ')}}
                </div>
                <author-social-links class="hidden-sm-down author-social-links" v-if="get(author, 'links')" :links="author.links"></author-social-links>
              </div>
            </onload-idle-queue>
            <br>
          </div>
        </div>
      </div>
      <field-date :nid="nid" :formatDateHTML="formatDateHTML"></field-date>
    </div>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import get from 'lodash/get'
  import forEach from 'lodash/forEach'
  import map from 'lodash/map'
  import isEmpty from 'lodash/isEmpty'

  import SvgAuthorExpert from 'static/svg/icon_author_expert.svg'
  import mounted from 'components/mixins/mounted'
  import FieldDate from 'components/node/fields/Date.vue'
  import SectionMixin from 'components/mixins/section'
  import { rewriteImageSrc, getArticleType } from 'mixins/utilities'

  export default {
    name: 'author-pane',
    data() {
      return {
        defaultImg: '/static/images/generic_author_image.jpg',
      }
    },
    props: {
      formatDateHTML: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [SectionMixin, mounted],
    methods: {
      isEmpty,
      get,
    },
    components: {
      FieldDate,
      SvgAuthorExpert,
      AuthorSocialLinks: () => import('components/term/AuthorSocialLinks.vue'),
    },
    computed: {
      ...mapState([
        'viewportIsDesktop',
      ]),
      sections() {
        const sections = []
        forEach(get(this, 'node.sections', []), (sectionLineage) => {
          const sectionList = map(sectionLineage, this.$store.getters.term)
          sections.push(sectionList)
        })
        return sections
      },
      borderColorClass() {
        if (this.articleType === 'survival-guide') return this.articleType
        return this.sectionColorClass
      },
      authors() {
        return map(get(this, 'node.authors', []), this.$store.getters.term)
      },
      author() {
        return this.$store.getters.term(get(this, 'node.authors[0]'))
      },
      isMultiAuthor() {
        return this.authors.length > 1
      },
      authorImage() {
        if (this.isMultiAuthor) return '/static/images/generic_multi_author_image.png'
        return rewriteImageSrc(get(this, 'author.images[0].style_500x500'))
      },
      articleType() {
        const articleType = getArticleType(this.node)
        const nodeType = get(this.node, 'type')
        if (articleType === 'default' && nodeType === 'person') return 'person'
        return articleType
      },
    },
  }
</script>
<style lang="scss" scoped>
  @keyframes fadeInDownAuthorBox {
    from {
      opacity: 0;
      transform: translate3d(0, -10%, 0);
    }

    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  .author-pane {
    @include respond-to("mobile") {
      margin-top: rem-calc(-54);
      text-align: center;
    }
    font-family: $barlow;

    .author-image {
      @include respond-to("mobile") {
        display: block;
        height: rem-calc(80);
        width: 100%;
        text-align: center;
        padding-top: rem-calc(16);
        margin-left: rem-calc(-10);
      }
      @include respond-to("desktop") {
        height: rem-calc(88);
        width: 100%;
      }
      padding-left: rem-calc(12);
      padding-top: rem-calc(12);
    }

    .image-border-mask,
    .author-mask {
      @include term-border-color();
      overflow: hidden;
      width: rem-calc(70);
      height: rem-calc(70);
      @include respond-to("desktop") {
        width: rem-calc(57);
        height: rem-calc(57);
      }
    }

    .author-info {
      @include respond-to("mobile") {
        width: 100%;
        margin-top: rem-calc(40);
      }

      @include respond-to("desktop") {
        &.review,
        &.default {
          margin-top: rem-calc(10);
        }
      }
    }

    .author-mask {
      @include respond-to("mobile") {
        display: inline-block;
        margin-bottom: rem-calc(16);
      }
      box-sizing: content-box;
      border-width: rem-calc(3);
      border-style: solid;
      background-color: $pastel-gray;
      overflow: hidden;
      transform: translateZ(0px) rotate(45deg);
      img {
        @include respond-to("desktop") {
          width: rem-calc(90);
          height: rem-calc(90);
          transform: rotate(-45deg) translateY(rem-calc(-22));
        }
        transform: rotate(-45deg) translateY(rem-calc(-27));
        width: rem-calc(108);
        height: rem-calc(108);

        &.multi-author{
          @include respond-to("mobile") {
            transform: rotate(-45deg) translateY(rem-calc(-26));
          }
        }
      }
    }

    .author-info-name:hover ~ .author-info-box-wrapper,
    .author-info-box-wrapper:hover {
      display: block;
      animation-name: fadeInDownAuthorBox;
      animation-duration: 0.5s;
    }

    .author-list {
      @include respond-to("desktop") {
        font-size: rem-calc(18);
        line-height: rem-calc(22);
      }
      font-size: rem-calc(14);
      line-height: rem-calc(16);
      margin-bottom: rem-calc(7);

      a {
        font-weight: bold;
      }
      .authors {
        display: inline-block;
        text-align: left;
        &:before {
          content: "by ";
        }
        .author-name {
          display: inline-block;
          text-align: left;
          position: relative;
          &:nth-child(n + 2) {
            display: block;
            margin-left: rem-calc(17);
            @include respond-to("desktop") {
              margin-left: rem-calc(21);
            }
          }
        }
      }
    }

    .author-info-box-wrapper {
      display: none;
      position: absolute;
      top: rem-calc(20);
      left: rem-calc(-22);
      z-index: 1;
      .author-info-box {
        color: $prussian-blue-2;
        width: rem-calc(330);
        background: $isabelline;
        border: 1px solid #c6c6c6;
        padding: rem-calc(15);
        margin-top: rem-calc(16);
        position: relative;
        /deep/ .author-social-links .links-wrapper {
          justify-content: flex-end;
          margin-top: rem-calc(25);

          a {
            background-color: $dark-gray;

            &:not(.active) {
              opacity: 0.4;
            }

            &.active {
              background-color: $rose;
            }
          }
        }
        &:after {
          content: '';
          display: block;
          position: absolute;
          top: rem-calc(-8);
          left: rem-calc(37);
          width: rem-calc(14);
          height: rem-calc(14);
          background: $isabelline;
          border-right: 1px solid  #c6c6c6;
          border-bottom: 1px solid #c6c6c6;
          transform:rotate(-135deg);
        }
        .name {
          font-size: rem-calc(20);
        }
        .role {
          font-size: rem-calc(16);
          font-weight: 500;
        }
        .expertise {
          font-size: rem-calc(16);
          font-weight: 500;
          margin-top: rem-calc(14);
          svg {
            width: rem-calc(13);
            height: rem-calc(13);
            margin-right: rem-calc(4);
            fill: $prussian-blue-2;
          }
        }

        &.opinion {
          background: $white;
          color: $prussian-blue-2;
          border-color: #dddddd;
          &:after {
            background: $white;
            border-color: #dddddd;
          }
          .expertise {
            svg {
              fill: $prussian-blue-2;
            }
          }
        }

        &.photo-essay ,
        &.survival-guide {
          background: $darker-gray-3;
          border-color: #767676;
          color: $white;
          &:after {
            background: $darker-gray-3;
            border-color: #767676;
          }
          .expertise {
            svg {
              fill: $white;
            }
          }
        }

        &.explainer {
          background: $black;
          border-color: #767676;
          color: $white;
          &:after {
            background: $black;
            border-color: #767676;
          }
          .expertise {
            svg {
              fill: $white;
            }
          }
        }

        &.person {
          background: $cadet-blue-2;
          color: $white;
          border-color: #6a839d;
          &:after {
            background: $cadet-blue-2;
            border-color: #6a839d;
          }
          .expertise {
            svg {
              fill: $white;
            }
          }
        }

        &.survival-guide {
          color: $white;
          border-color: $white;
          &:after {
            border-color: $white;
          }
          .expertise {
            svg {
              fill: $white;
            }
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// components/node/AuthorPane.vue